/**
 * useVoiceCall — initialization hook for Twilio voice calling.
 *
 * Auto-initializes the voice subsystem when:
 *   1. TPA module is loaded and authenticated (Twilio)
 *   2. The app code indicates a Twilio extension
 *
 * Returns call state and actions for UI components.
 * Handles cleanup on unmount.
 */

import { useEffect, useRef } from 'react';
import { useCallStore } from '@/stores/call-store';
import { useModuleStore } from '@/stores/module-store';
import { useAppStore } from '@/stores/app-store';
import { useAuthStore, getEffectiveUid } from '@/stores/auth-store';
import { useSettingsStore } from '@/stores/settings-store';
import { TwilioAPI } from '@/modules/tpa/twilio/twilio-api';

// Overridable for local emulator testing: point VITE_VOICE_WEBHOOK_BASE_URL
// at a public tunnel to the functions emulator (e.g. https://<ngrok>/ulgebra-
// license/us-central1/requestByExternalService) and the TwiML app's VoiceUrl
// is rewritten to it on the next app load. Remove the override + reload to
// restore production routing the same way.
const WEBHOOK_BASE_URL =
  import.meta.env.VITE_VOICE_WEBHOOK_BASE_URL || 'https://api.ulgebra.com/v1/proxyrequest';

export function useVoiceCall() {
  const initRef = useRef(false);

  // Wait until settings have loaded — this resolves selectedAdminUserId
  // so we know if the current user is a shared user before initializing voice
  const settingsLoaded = useSettingsStore((s) => s.settingsLoaded);
  const enableVoiceCalling = useSettingsStore((s) => s.enableVoiceCalling);

  // App context
  const extensionName = useAppStore((s) => s.extensionName);
  const appConfig = useAppStore((s) => s.appConfig);
  const appCodeSAAS = useAppStore((s) => s.appCodeSAAS);

  // Auth
  const user = useAuthStore((s) => s.user);
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);
  const tpaAuthenticated = useModuleStore((s) => s.tpaAuthenticated);
  const webhookToken = useModuleStore((s) => s.webhookToken);
  const fetchWebhookToken = useModuleStore((s) => s.fetchWebhookToken);

  // Call store selectors
  const softphoneState = useCallStore((s) => s.softphoneState);
  const isAvailable = useCallStore((s) => s.isAvailable);
  const activeCalls = useCallStore((s) => s.activeCalls);
  const callerIds = useCallStore((s) => s.callerIds);
  const callManagerOpen = useCallStore((s) => s.callManagerOpen);

  // Call store actions
  const initializeVoice = useCallStore((s) => s.initializeVoice);
  const destroyVoice = useCallStore((s) => s.destroyVoice);
  const makeCall = useCallStore((s) => s.makeCall);
  const acceptCall = useCallStore((s) => s.acceptCall);
  const endCall = useCallStore((s) => s.endCall);
  const ignoreCall = useCallStore((s) => s.ignoreCall);
  const muteCall = useCallStore((s) => s.muteCall);
  const holdCall = useCallStore((s) => s.holdCall);
  const toggleRecording = useCallStore((s) => s.toggleRecording);
  const sendDigits = useCallStore((s) => s.sendDigits);
  const toggleCallManager = useCallStore((s) => s.toggleCallManager);

  // Call store error
  const voiceError = useCallStore((s) => s.voiceError);

  // Determine if this is a Twilio extension that supports voice
  const isTwilioExt = extensionName?.startsWith('twilio') ?? false;

  // Shared user = using another admin's TPA credentials (selectedAdminUserId is set and differs from own uid)
  const isSharedUser = !!(selectedAdminUserId && user && selectedAdminUserId !== user.uid);

  // Initialize voice as soon as TPA is authenticated (non-blocking)
  // No longer waits for isAvailable — softphone registers on page load
  useEffect(() => {
    if (
      !settingsLoaded ||
      !isTwilioExt ||
      !tpaAuthenticated ||
      !enableVoiceCalling ||
      !extensionName ||
      !appConfig?.orgId ||
      !user ||
      initRef.current
    ) {
      return;
    }

    // Build webhook base URL — token is optional here because the TwiML app
    // may already exist with the token baked into its VoiceUrl.
    // Token is only needed when creating/updating the TwiML app.
    const webhookBase = `${WEBHOOK_BASE_URL}?a=${extensionName}&o=${appConfig.orgId}${
      webhookToken?.tpa ? `&t=${webhookToken.tpa}` : ''
    }&requestContext=TWILIO_VOICE&_ua_action=call-add`;

    // Derive SaaS service name for TwiML app friendly name
    const saasServiceName = appCodeSAAS || 'CRM';

    // Create API instance for voice operations
    const api = new TwilioAPI(extensionName);

    initRef.current = true;

    // Resolve account SID then initialize
    (async () => {
      try {
        // If webhook token isn't available yet, fetch it before init
        // (needed when creating/updating the TwiML app's VoiceUrl)
        if (!webhookToken?.tpa && extensionName) {
          console.log('[useVoiceCall] Fetching webhook token before voice init...');
          await fetchWebhookToken(extensionName);
          // Re-read the token from the store after fetch
          const freshToken = useModuleStore.getState().webhookToken;
          if (freshToken?.tpa) {
            const webhookBaseWithToken = `${WEBHOOK_BASE_URL}?a=${extensionName}&o=${appConfig.orgId}&t=${freshToken.tpa}`;
            // Use the token-enriched URL
            const accountsResp = await api.getAccounts();
            const accountSid = accountsResp.data?.accounts?.[0]?.sid;
            if (!accountSid) {
              console.warn('[useVoiceCall] No Twilio account found, skipping voice init');
              return;
            }
            await initializeVoice(api, accountSid, extensionName, webhookBaseWithToken, saasServiceName, isSharedUser);
            const finalState = useCallStore.getState().softphoneState;
            if (finalState === 'error') {
              console.error('[useVoiceCall] Voice initialization failed (softphoneState = error)');
              initRef.current = false;
            } else {
              console.log('[useVoiceCall] Voice initialized successfully, state:', finalState);
            }
            return;
          }
        }

        const accountsResp = await api.getAccounts();
        const accountSid = accountsResp.data?.accounts?.[0]?.sid;
        if (!accountSid) {
          console.warn('[useVoiceCall] No Twilio account found, skipping voice init');
          return;
        }

        await initializeVoice(api, accountSid, extensionName, webhookBase, saasServiceName, isSharedUser);

        // Check actual result — initializeVoice sets softphoneState but doesn't throw on failure
        const finalState = useCallStore.getState().softphoneState;
        if (finalState === 'error') {
          console.error('[useVoiceCall] Voice initialization failed (softphoneState = error)');
          initRef.current = false; // Allow retry on next dependency change
        } else {
          console.log('[useVoiceCall] Voice initialized successfully, state:', finalState);
        }
      } catch (error) {
        console.error('[useVoiceCall] Voice initialization failed:', error);
        initRef.current = false; // Allow retry
      }
    })();

    return () => {
      // Cleanup on unmount
      destroyVoice();
      initRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsLoaded, isTwilioExt, tpaAuthenticated, enableVoiceCalling, extensionName, appConfig?.orgId, user, selectedAdminUserId]);

  const hasActiveCall = activeCalls.some(
    (c) =>
      c.status === 'connected' ||
      c.status === 'connecting' ||
      c.status === 'ringing' ||
      c.status === 'on-hold'
  );

  return {
    // State
    softphoneState,
    isAvailable,
    activeCalls,
    hasActiveCall,
    callerIds,
    callManagerOpen,
    isTwilioExt,
    voiceError,

    // Actions
    makeCall,
    acceptCall,
    endCall,
    ignoreCall,
    muteCall,
    holdCall,
    toggleRecording,
    sendDigits,
    toggleCallManager,
  };
}
